// Eskom PARCC — the Wednesday Composer model (Lindiwe, 10 Sep 2026).
//
// ONE (caller-scoped) pull → a CUMULATIVE detailed section (every quarter up to
// the through-quarter, one set of tables) PLUS one per-quarter divisional slice.
// Every figure comes from the SAME based rows and the SAME SummaryRow/GapBreakdown
// builders the deck renders, so the KPI cards and slide-5 charts can never disagree
// with the tables (the stored-level defect in buildReportModel's datasetCoverage /
// divisionStats is avoided here — this path never calls them). Deterministic: the
// date strings + generation timestamp are passed in. Pure; tested in
// test/features/reports/buildQuarterlyReportModel.test.ts.
import type { Transaction } from '../../data/types';
import type { FindingRead } from '../../lib/db';
import { DISTRIBUTION_LEVELS, levelColour } from '../dashboard/palette';
import { summariseFindings, genuineFindings, type FindingSummary } from '../../domain/findings';
import { buildSelfDeclaredRefs } from '../../domain/gaps';
import {
  withLevels,
  divisionSummaryRows,
  datasetSummaryRows,
  divisionGapBreakdownRows,
  divisionReviewUpdates,
  canonicalDivisionSummaryRows,
  canonicalDivisionGapRows,
  sumRows,
  SUMMARY_ADDITIVE_FIELDS,
  type SummaryRow,
  type GapBreakdownRow,
  type DivisionReviewUpdate,
} from './deckSections';
import {
  type ReportScope,
  type Quarter,
  DECK_DIVISION_ORDER,
  deckDivisionLabel,
  scopeRows,
  rowsForQuarter,
  emptyQuartersInScope,
  slideTitles,
  quarterSlideTitle,
  reviewUpdateTitle,
  type ScopedRows,
} from './reportScope';
import {
  narrativeKey,
  draftInputs,
  narrativeIsStale,
  draftGapsSd,
  draftDivisionalIntro,
  draftDivisionalFooter,
  draftKpiCards,
  draftQuarterIntro,
  draftQuarterFooter,
  draftQuarterRangeLabel,
  draftReviewUpdate,
  type SlideKey,
} from './narratives';
import { quartersThrough, rangeLabel, QUARTER_MONTHS } from '../../domain/fiscal';
import { buildReportModel, type ReportModel, type ReportMeta, type DistributionRow } from './buildReportModel';

/** A persisted (edited) narrative row, or a component-state stand-in (Phase 1a). */
export interface NarrativeInput {
  key: string;
  text: string;
  edited: boolean;
  draftInputs?: Record<string, number> | null;
}

/** A Reports-to-Management status row (Phase 1b table, or component state in 1a). */
export interface RtmStatusRow {
  fy: string;
  quarter: string;
  division: string;
  submitted?: string;
  signedOff?: string;
  note?: string;
}

/** One resolved narrative: the text to render, whether it was edited, and if stale. */
export interface ResolvedNarrative {
  text: string;
  source: 'edited' | 'auto';
  stale: boolean;
}

export interface ChartCategory {
  label: string;
  records: number;
  /** with-findings (source chart) / flagged (division chart) — gapsIdentified. */
  value: number;
}

export interface DeckSection {
  gapsSd: GapBreakdownRow[];
  divisional: SummaryRow[];
  bySource: SummaryRow[];
  kpiCards: SummaryRow;
  distribution: DistributionRow[];
  charts: { bySourceRecords: ChartCategory[]; byDivisionRecords: ChartCategory[] };
  divisionUpdates: DivisionReviewUpdate[];
  findings: FindingSummary;
}

export interface QuarterSlice {
  quarter: Quarter;
  rangeLabel: string;
  divisional: SummaryRow[];
  kpis: SummaryRow;
}

export interface ReadinessLine {
  kind: 'info' | 'warn' | 'error';
  text: string;
  count?: number;
}

export interface QuarterlyReportModel {
  meta: {
    fy: string;
    throughQuarter: Quarter;
    quarters: Quarter[];
    asOfDate: string;
    dateLong: string;
    rangeLabel: string;
    division: string;
    generatedAtIso: string;
    template: 'v4';
    fyRecorded: boolean;
  };
  titles: ReturnType<typeof slideTitles> & {
    quarters: Record<string, string>;
    reviewUpdates: Record<string, string>;
  };
  cumulative: DeckSection;
  quarters: QuarterSlice[];
  rtmStatus: RtmStatusRow[];
  narratives: Record<string, ResolvedNarrative>;
  /** The current numeric fingerprint per narrative key (for the composer's save + staleness). */
  fingerprints: Record<string, Record<string, number>>;
  readiness: ReadinessLine[];
  invariants: { ok: boolean; warnings: string[] };
  appendix?: ReportModel;
}

export interface QuarterlyMeta {
  dateLong: string;
  generatedAtIso: string;
}

/** Distribution wedges from based rows' effective levels; sums to population. */
function buildDistribution(based: readonly Transaction[]): DistributionRow[] {
  const total = based.length;
  const byLevel = new Map<string, number>();
  for (const t of based) {
    const level = String(t.complianceLevel ?? 'Pending Review');
    byLevel.set(level, (byLevel.get(level) ?? 0) + 1);
  }
  const rows: DistributionRow[] = [];
  for (const level of DISTRIBUTION_LEVELS) {
    const count = byLevel.get(level as string) ?? 0;
    if (count > 0) {
      rows.push({
        level: level as string,
        count,
        pct: total > 0 ? Math.round((count / total) * 100) : 0,
        value: 0,
        colour: levelColour(level as string),
      });
    }
  }
  // Everything the distribution levels do NOT cover (e.g. "To delete") as one
  // trailing wedge so the pie sums to population.
  const covered = rows.reduce((s, r) => s + r.count, 0);
  if (total - covered > 0) {
    rows.push({ level: 'Marked to delete', count: total - covered, pct: Math.round(((total - covered) / total) * 100), value: 0, colour: '#83725B' });
  }
  return rows;
}

/**
 * The findings in scope for a based row set (its transactions' non-deleted findings).
 */
function scopeFindings(based: readonly Transaction[], findings: readonly FindingRead[]): FindingRead[] {
  const refs = new Set(based.map((t) => t.referenceNo));
  return findings.filter((f) => f.referenceNo && refs.has(f.referenceNo));
}

/**
 * Refs with a genuine finding (incl. resolved) → the summary tables count a buyer
 * fix (now Compliant, finding on record) as an identified-and-addressed gap, not a
 * forgotten one (RTM Workstream I). Applied to the cumulative AND every quarter
 * slice from the same finding set, so the per-quarter figures still sum to the
 * cumulative (invariant I2).
 */
function findingRefsFor(based: readonly Transaction[], findings: readonly FindingRead[]): Set<string> {
  return new Set(
    genuineFindings(scopeFindings(based, findings))
      .map((f) => f.referenceNo)
      .filter((r): r is string => r != null),
  );
}

/** A DeckSection from an already-based (effective-level) row set + scoped findings. */
function buildSection(based: readonly Transaction[], findings: readonly FindingRead[], sdRefs: ReadonlySet<string>): DeckSection {
  const scopedFindings = scopeFindings(based, findings);
  const findingRefs = findingRefsFor(based, findings);
  const divisional = canonicalDivisionSummaryRows(divisionSummaryRows(based, undefined, findingRefs));
  const gapsSd = canonicalDivisionGapRows(divisionGapBreakdownRows(based));
  const bySource = datasetSummaryRows(based, undefined, findingRefs);
  const kpiCards = divisional.find((r) => r.key === 'Total') ?? sumRows(divisional.filter((r) => r.key !== 'Total'));
  const rawUpdates = divisionReviewUpdates(based);
  const updateByDiv = new Map(rawUpdates.map((u) => [u.division, u]));
  const divisionUpdates: DivisionReviewUpdate[] = DECK_DIVISION_ORDER.filter((d) => updateByDiv.has(d))
    .map((d) => updateByDiv.get(d)!)
    .concat(rawUpdates.filter((u) => !DECK_DIVISION_ORDER.includes(u.division)));
  const bySourceRecords: ChartCategory[] = bySource
    .filter((r) => r.key !== 'Total')
    .map((r) => ({ label: r.key, records: r.population, value: r.gapsIdentified }));
  const byDivisionRecords: ChartCategory[] = divisional
    .filter((r) => r.key !== 'Total')
    .map((r) => ({ label: deckDivisionLabel(r.key), records: r.population, value: r.gapsIdentified }));
  return {
    gapsSd,
    divisional,
    bySource,
    kpiCards,
    distribution: buildDistribution(based),
    charts: { bySourceRecords, byDivisionRecords },
    divisionUpdates,
    findings: summariseFindings(scopedFindings, sdRefs),
  };
}

/** Compute the self-consistency invariant warnings (never thrown — surfaced). */
function computeInvariants(cumulative: DeckSection, quarters: QuarterSlice[]): { ok: boolean; warnings: string[] } {
  const w: string[] = [];
  const cumByDiv = new Map(cumulative.divisional.map((r) => [r.key, r]));
  // I2 — quarter slices partition the cumulative per division per additive field.
  for (const key of [...DECK_DIVISION_ORDER, 'Total']) {
    const cum = cumByDiv.get(key);
    if (!cum) continue;
    for (const f of SUMMARY_ADDITIVE_FIELDS) {
      const summed = quarters.reduce((s, q) => s + (q.divisional.find((r) => r.key === key)?.[f] ?? 0), 0);
      // Tolerance, not strict !==: `ieValue` is a Rand FLOAT, and summing it per-quarter
      // then across quarters diverges from the cumulative sum in the last ULP (float
      // addition is not associative), which tripped the banner spuriously (15 Sep 2026).
      // Counts are integers, so 1e-6 never masks a real off-by-N.
      if (Math.abs(summed - cum[f]) > 1e-6) w.push(`I2: ${key}.${f} cumulative ${cum[f]} ≠ Σ quarters ${summed}`);
    }
  }
  // I3 — the three Total rows agree on outstanding + self-declared.
  const dTot = cumByDiv.get('Total');
  const gTot = cumulative.gapsSd.find((r) => r.key === 'Total');
  const sTot = cumulative.bySource.find((r) => r.key === 'Total');
  if (dTot && gTot && sTot) {
    if (!(dTot.outstanding === gTot.outstanding && dTot.outstanding === sTot.outstanding))
      w.push(`I3: Total outstanding disagree — divisional ${dTot.outstanding}, gaps ${gTot.outstanding}, source ${sTot.outstanding}`);
    if (!(dTot.selfDeclared === gTot.selfDeclared && dTot.selfDeclared === sTot.selfDeclared))
      w.push(`I3: Total self-declared disagree — divisional ${dTot.selfDeclared}, gaps ${gTot.selfDeclared}, source ${sTot.selfDeclared}`);
  }
  // I4 — per-division gaps row == divisional row (outstanding + SD).
  const gByDiv = new Map(cumulative.gapsSd.map((r) => [r.key, r]));
  for (const d of DECK_DIVISION_ORDER) {
    const div = cumByDiv.get(d);
    const gap = gByDiv.get(d);
    if (!div || !gap) continue;
    if (div.outstanding !== gap.outstanding) w.push(`I4: ${d} outstanding divisional ${div.outstanding} ≠ gaps ${gap.outstanding}`);
    if (!(div.selfDeclared === gap.selfDeclared && div.selfDeclared === div.selfDeclaredGaps))
      w.push(`I4: ${d} self-declared divisional ${div.selfDeclared} / gaps ${gap.selfDeclared} / selfDeclaredGaps ${div.selfDeclaredGaps} disagree`);
  }
  // I6 — distribution sums to population; chart series equal the table columns.
  if (dTot) {
    const distSum = cumulative.distribution.reduce((s, r) => s + r.count, 0);
    if (distSum !== dTot.population) w.push(`I6: distribution ${distSum} ≠ population ${dTot.population}`);
    const chartRecords = cumulative.charts.byDivisionRecords.reduce((s, c) => s + c.records, 0);
    if (chartRecords !== dTot.population) w.push(`I6: division-chart records ${chartRecords} ≠ population ${dTot.population}`);
  }
  // I9 — reviewed reconciliation per division.
  for (const r of cumulative.divisional) {
    const parts = r.compliant + r.selfDeclared + r.outstanding + r.notApplicable + r.unclassified;
    if (parts !== r.reviewed) w.push(`I9: ${r.key} reviewed ${r.reviewed} ≠ compliant+SD+outstanding+NA+unclassified ${parts}`);
  }
  return { ok: w.length === 0, warnings: w };
}

/** Data-derived readiness lines (the composer adds the persistence/import lines). */
function buildReadiness(scoped: ScopedRows, scope: ReportScope, inScope: readonly Transaction[], warnings: string[]): ReadinessLine[] {
  const lines: ReadinessLine[] = [];
  lines.push({ kind: 'info', text: `${inScope.length} transactions in scope (${scope.fy} ${scope.throughQuarter === 'Q1' ? 'Q1' : 'Q1 – ' + scope.throughQuarter})`, count: inScope.length });
  if (!scoped.fyRecorded)
    lines.push({ kind: 'warn', text: 'Financial year is not recorded on this population — run the FY backfill before FY28 rows land.' });
  const surf = scoped.surfaced;
  if (surf.blankYear.length) lines.push({ kind: 'warn', text: `${surf.blankYear.length} row(s) have no financial year`, count: surf.blankYear.length });
  if (surf.unrecognisedYear.length) lines.push({ kind: 'warn', text: `${surf.unrecognisedYear.length} row(s) have an unrecognised financial year`, count: surf.unrecognisedYear.length });
  if (surf.otherFy.length) lines.push({ kind: 'warn', text: `${surf.otherFy.length} row(s) belong to another financial year`, count: surf.otherFy.length });
  if (surf.afterThroughQuarter.length) lines.push({ kind: 'warn', text: `${surf.afterThroughQuarter.length} row(s) fall after ${scope.throughQuarter} (loaded early)`, count: surf.afterThroughQuarter.length });
  if (surf.blankPeriod.length) lines.push({ kind: 'warn', text: `${surf.blankPeriod.length} row(s) have no quarter and cannot be placed`, count: surf.blankPeriod.length });
  for (const q of emptyQuartersInScope(inScope, scope)) lines.push({ kind: 'error', text: `${q} is in scope but has no transactions loaded` });
  const reviewedLinksOut = inScope.filter((t) => (t.reviewed === true || String(t.reviewStatus ?? '').toLowerCase() === 'reviewed')).filter((t) => {
    // A reviewed row still showing a link as outstanding.
    return !(t.linkProvided === true) && !String(t.link ?? '').trim();
  }).length;
  if (reviewedLinksOut > 0) lines.push({ kind: 'warn', text: `${reviewedLinksOut} reviewed transaction(s) still have links outstanding`, count: reviewedLinksOut });
  for (const warn of warnings) lines.push({ kind: 'error', text: warn });
  return lines;
}

export function buildQuarterlyReportModel(input: {
  transactions: readonly Transaction[];
  findings: readonly FindingRead[];
  scope: ReportScope;
  meta: QuarterlyMeta;
  narratives?: readonly NarrativeInput[];
  rtmStatus?: readonly RtmStatusRow[];
  effectiveLevelByRef?: ReadonlyMap<string, string>;
}): QuarterlyReportModel {
  const { transactions, findings, scope, meta } = input;
  const levelOf = input.effectiveLevelByRef
    ? (t: Transaction) => input.effectiveLevelByRef!.get(t.referenceNo) ?? t.complianceLevel ?? ''
    : undefined;

  const nonDeleted = transactions.filter((t) => !t.deleted);
  const scoped = scopeRows(nonDeleted, scope);
  const basedInScope = withLevels(scoped.inScope, levelOf);
  const sdRefs = buildSelfDeclaredRefs(scoped.inScope);

  const cumulative = buildSection(basedInScope, findings, sdRefs);

  const span = quartersThrough(scope.throughQuarter);
  // A per-quarter slice is emitted only when the cumulative span is more than Q1
  // (through-Q1: the cumulative slides ARE the Q1 slides — no duplicate).
  const sliceQuarters = span.length > 1 ? span : [];
  const quarters: QuarterSlice[] = sliceQuarters.map((q) => {
    const rows = rowsForQuarter(scoped.inScope, q);
    const based = withLevels(rows, levelOf);
    const divisional = canonicalDivisionSummaryRows(
      divisionSummaryRows(based, undefined, findingRefsFor(based, findings)),
    );
    return {
      quarter: q,
      rangeLabel: QUARTER_MONTHS[q].label,
      divisional,
      kpis: divisional.find((r) => r.key === 'Total') ?? sumRows(divisional.filter((r) => r.key !== 'Total')),
    };
  });

  const invariants = computeInvariants(cumulative, quarters);

  // ---- narratives -------------------------------------------------------
  const editedByKey = new Map((input.narratives ?? []).map((row) => [row.key, row]));
  const scopeKey = scope.throughQuarter === 'Q1' ? 'CUM:Q1' : `CUM:${scope.throughQuarter}`;
  const narratives: Record<string, ResolvedNarrative> = {};
  const fingerprints: Record<string, Record<string, number>> = {};
  const resolve = (slideKey: SlideKey, sk: string, division: string, draft: string, inputs: Record<string, number>): void => {
    const key = narrativeKey(scope.fy, sk, slideKey, division);
    fingerprints[key] = inputs;
    const row = editedByKey.get(key);
    if (row && row.edited) {
      narratives[key] = { text: row.text, source: 'edited', stale: narrativeIsStale(row.draftInputs, inputs) };
    } else {
      narratives[key] = { text: draft, source: 'auto', stale: false };
    }
  };
  const cumTotal = cumulative.kpiCards;
  const gapsTotal = cumulative.gapsSd.find((r) => r.key === 'Total') ?? cumulative.gapsSd[cumulative.gapsSd.length - 1];
  resolve('gaps-sd', scopeKey, '', draftGapsSd(cumulative.gapsSd), draftInputs(gapsTotal ?? ({} as GapBreakdownRow)));
  resolve('divisional-intro', scopeKey, '', draftDivisionalIntro(cumTotal), draftInputs(cumTotal));
  resolve('divisional-footer', scopeKey, '', draftDivisionalFooter(cumTotal), draftInputs(cumTotal));
  resolve('kpi-cards', scopeKey, '', draftKpiCards(cumTotal), draftInputs(cumTotal));
  for (const slice of quarters) {
    resolve('quarter-intro', slice.quarter, '', draftQuarterIntro(slice.kpis), draftInputs(slice.kpis));
    resolve('quarter-footer', slice.quarter, '', draftQuarterFooter(slice.kpis), draftInputs(slice.kpis));
    resolve('quarter-range-label', slice.quarter, '', draftQuarterRangeLabel(slice.quarter), {});
  }
  for (const u of cumulative.divisionUpdates) {
    const total = u.summary.find((r) => r.key === 'Total');
    if (total) resolve('review-update', scopeKey, u.division, draftReviewUpdate(total), draftInputs(total));
  }

  // ---- titles -----------------------------------------------------------
  const baseTitles = slideTitles(scope, meta.dateLong);
  const quarterTitles: Record<string, string> = {};
  for (const slice of quarters) quarterTitles[slice.quarter] = quarterSlideTitle(scope, slice.quarter, meta.dateLong);
  const reviewUpdateTitles: Record<string, string> = {};
  for (const u of cumulative.divisionUpdates) reviewUpdateTitles[u.division] = reviewUpdateTitle(scope, u.division, meta.dateLong);

  const readiness = buildReadiness(scoped, scope, scoped.inScope, invariants.warnings);

  // ---- appendix (optional; the legacy analytics model) ------------------
  let appendix: ReportModel | undefined;
  if (scope.appendix) {
    const appMeta: ReportMeta = {
      dateLong: meta.dateLong,
      periodTitle: `${scope.fy} ${scope.throughQuarter === 'Q1' ? 'Q1' : 'Q1 – ' + scope.throughQuarter}`,
      scopeLabel: scope.division || rangeLabel('Q1', scope.throughQuarter),
      divisionFilter: scope.division,
      generatedAtIso: meta.generatedAtIso,
    };
    const lines = genuineFindings(findings).filter((f) => new Set(scoped.inScope.map((t) => t.referenceNo)).has(f.referenceNo ?? ''));
    appendix = buildReportModel(scoped.inScope, lines, appMeta, [], [], input.effectiveLevelByRef);
  }

  return {
    meta: {
      fy: scope.fy,
      throughQuarter: scope.throughQuarter,
      quarters: span,
      asOfDate: scope.asOfDate,
      dateLong: meta.dateLong,
      rangeLabel: rangeLabel('Q1', scope.throughQuarter),
      division: scope.division,
      generatedAtIso: meta.generatedAtIso,
      template: 'v4',
      fyRecorded: scoped.fyRecorded,
    },
    titles: { ...baseTitles, quarters: quarterTitles, reviewUpdates: reviewUpdateTitles },
    cumulative,
    quarters,
    rtmStatus: [...(input.rtmStatus ?? [])],
    narratives,
    fingerprints,
    readiness,
    invariants,
    appendix,
  };
}
